/* eslint-disable prefer-template */
import Logger from 'es6/services/logger';
import ServerTimeManager from 'es6/services/server-time-manager';

export const ResultTypes = Object.freeze({
  SEEN:               'Seen',
  CORRECT:            'Correct',
  EASY:               'Easy',
  OK:                 'Ok',
  HARD:               'Hard',
  ALMOST_CORRECT:     'AlmostCorrect',
  CORRECT_WITH_HINT:  'CorrectWithHint',
  INCORRECT:          'Incorrect',
  YIELD_ON_RECALL:    'YieldOnRecall',
  YIELD_ON_QUIZ:      'YieldOnQuiz',
  TIMEOUT:            'TimeOutFailure',
});

export const CorrectTypes = [
  ResultTypes.SEEN,
  ResultTypes.CORRECT,
  ResultTypes.CORRECT_WITH_HINT,
  ResultTypes.OK,
  ResultTypes.EASY,
  ResultTypes.HARD,
];

export class QuizResult {
  constructor(contentDomain, contentId, goalId, leQuizType = null) {
    this._currentPhase = null;
    this._phaseStart = null;
    this._timingInfo = {};
    this._pauseStarted = null;
    this._pauseDurationInPhase = 0;

    // Quiz data to return to the learning engine.
    this.contentDomain = contentDomain;
    this.contentId = contentId;
    this.goalId = goalId;
    this.leQuizType = leQuizType;
    this.quizData = {};
  }

  setResult(result) {
    if (!_.contains(_.values(ResultTypes), result)) {
      throw new Error('Unrecognized result type: ' + result);
    }
    this.leQuizResult = result;
  }

  isCorrect() {
    return _.contains(CorrectTypes, this.leQuizResult);
  }

  isTimeout() {
    return this.leQuizResult === ResultTypes.TIMEOUT;
  }

  beginPhase(phaseName) {
    // Set the occurredAt value if this is the first phase.
    if (!this.occurredAt) {
      this.occurredAt = ServerTimeManager.currentTimestamp();
    }

    this.endCurrentPhase();
    this._pauseDurationInPhase = 0;

    this._currentPhase = phaseName;
    this._phaseStart = this._nowTime();
  }

  isPhase(phaseName) {
    return this._currentPhase === phaseName;
  }

  endCurrentPhase() {
    if (!this._currentPhase) { return; }

    // Calculate duration of phase.
    const now = this._nowTime();
    let duration = now - this._phaseStart - this._pauseDurationInPhase;
    // Ensure phase has non-negative duration.
    if (duration < 0) {
      Logger.error('QuizResult', 'Negative duration logged in Quiz Result.', duration, now, this._phaseStart, this._pauseDurationInPhase);
      duration = 0;
    }
    this._timingInfo[this._currentPhase + 'TimeMillis'] = duration;

    this._currentPhase = null;
    this._phaseStart = null;
  }

  /**
   * Finalizes the quiz result and prepares the object to be sent
   * to the server.
   *
   * @returns {QuizResult}
   */
  finalize() {
    if (!this.leQuizType) {
      throw new ReferenceError('QuizType must be defined.');
    }

    // End the current phase in case the quiz is being force finished.
    this.endCurrentPhase();

    // Finalize timing data (ignore studyTimeMillis as that is only time
    // spent on the relearn screen, not total time that the server wants).
    _.extend(this, _.omit(this._timingInfo, 'studyTimeMillis'));

    // Calculate total study time.
    this.studyTimeMillis = _.values(this._timingInfo).reduce((a, b) => a + b, 0);

    // Finally remove private properties.
    delete this._currentPhase;
    delete this._phaseStart;
    delete this._timingInfo;
    delete this._pauseStarted;
    delete this._pauseDurationInPhase;

    return this;
  }

  pause() {
    this._pauseStarted = this._nowTime();
  }

  resume() {
    // Item may have been started during a pause. We will ignore the resume
    // event in this case. Note this could cause timing to be off by a few ms.
    if (!this._pauseStarted) {
      return;
    }

    this._pauseDurationInPhase += this._nowTime() - this._pauseStarted;
    this._pauseStarted = null;
  }

  _nowTime() {
    return new Date().getTime();
  }
}

export default QuizResult;
